import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Search, ExternalLink, LifeBuoy } from "lucide-react";
import ErrorHelpService, { ErrorCodeEntry } from "../../_services/ErrorHelpService";
import "./error-help.css";

/**
 * Error Code Help — admins paste the short code a student reported
 * (e.g. "ATL-1001" from "…contact your administrator and share code
 * ATL-1001") and get the exact cause plus step-by-step resolution,
 * with a deep link to the screen where the fix happens.
 *
 * Registry lives in nodefork (error-code-registry.ts), served via
 * GET /v1/error-codes[/:code]. Deep-linkable: /error-help?code=ATL-1001.
 */
const ErrorHelpPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [input, setInput] = useState("");
  const [entry, setEntry] = useState<ErrorCodeEntry | null>(null);
  const [all, setAll] = useState<ErrorCodeEntry[]>([]);
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const lookup = async (code: string) => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    setLooking(true);
    setLookupError(null);
    setEntry(null);
    try {
      setEntry(await ErrorHelpService.lookup(cleaned));
    } catch (e: any) {
      setLookupError(
        e?.response?.data?.error?.message ||
          e?.message ||
          "Could not look up that code. Check your connection and try again.",
      );
    } finally {
      setLooking(false);
    }
  };

  useEffect(() => {
    ErrorHelpService.list()
      .then(setAll)
      .catch(() => setAll([]));
    const code = new URLSearchParams(location.search).get("code");
    if (code) {
      setInput(code.toUpperCase());
      void lookup(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void lookup(input);
  };

  const pick = (code: string) => {
    setInput(code);
    void lookup(code);
  };

  return (
    <div className="error-help">
      <div className="eh-header">
        <h1>
          <LifeBuoy size={20} style={{ marginRight: 8, verticalAlign: "-3px" }} />
          Error Code Help
        </h1>
        <p className="eh-subtitle">
          When something goes wrong for a student, they see a friendly message with a short code
          (for example &quot;…share code ATL-1001 with your administrator&quot;). Enter that code
          here to see exactly what happened and how to fix it — most fixes are a settings screen
          away, no support ticket needed.
        </p>
      </div>

      <div className="eh-card">
        <form className="eh-lookup-row" onSubmit={submit}>
          <input
            className="eh-code-input"
            type="text"
            placeholder="e.g. ATL-1001"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            spellCheck={false}
            autoFocus
          />
          <button type="submit" className="eh-btn" disabled={looking || !input.trim()}>
            <Search size={15} /> {looking ? "Looking up…" : "Look up"}
          </button>
        </form>
        {lookupError && <div className="eh-error">{lookupError}</div>}
      </div>

      {entry && (
        <div className="eh-card">
          <div>
            <span className="eh-result-code">{entry.code}</span>
            <span className="eh-result-area">{entry.area}</span>
          </div>
          <div className="eh-result-title">{entry.title}</div>

          <div className="eh-section-label">What the student saw</div>
          <div className="eh-student-msg">&quot;{entry.studentMessage}&quot;</div>

          <div className="eh-section-label">What is actually wrong</div>
          <div className="eh-cause">{entry.cause}</div>

          <div className="eh-section-label">How to fix it</div>
          <ol className="eh-steps">
            {entry.adminSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {entry.fixScreen && (
            <button
              type="button"
              className="eh-fix-link"
              onClick={() => history.push(entry.fixScreen!.path)}
            >
              Open {entry.fixScreen.label} <ExternalLink size={14} />
            </button>
          )}
        </div>
      )}

      {all.length > 0 && (
        <div className="eh-card">
          <div className="eh-section-label" style={{ marginTop: 0 }}>All known codes</div>
          {all.map((e) => (
            <button key={e.code} type="button" className="eh-browse-item" onClick={() => pick(e.code)}>
              <span className="eh-browse-code">{e.code}</span>
              <span className="eh-browse-title">{e.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ErrorHelpPage;
